import React, { useCallback, useEffect, useState } from 'react';
import { apiFetch, apiJson } from '../apiClient';
import { normalizeRole } from '../utils/userLocale';
import './BranchesPage.css';
import {
  TANZANIA_REGIONS,
  getDistrictsByRegion,
  getRegionName,
  getDistrictNameResolved,
} from '../utils/tanzaniaLocations';

const emptyBranchForm = () => ({ name: '', region: '', district: '', street: '' });

const branchToForm = (branch) => ({
  name: branch?.name || '',
  region: branch?.region || '',
  district: branch?.district || '',
  street: branch?.street || '',
});

function BranchFormFields({ form, setForm, busy, districtOptions, idPrefix = '' }) {
  return (
    <>
      <label htmlFor={`${idPrefix}branch-name`}>
        Branch name
        <input
          id={`${idPrefix}branch-name`}
          required
          value={form.name}
          onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
          placeholder="e.g. Kibaha plant, Depot B"
          disabled={busy}
        />
      </label>
      <div className="branches-form-row">
        <label htmlFor={`${idPrefix}branch-region`}>
          Region (Mkoa)
          <select
            id={`${idPrefix}branch-region`}
            value={form.region}
            onChange={(e) => setForm((f) => ({ ...f, region: e.target.value, district: '' }))}
            disabled={busy}
            className="branches-select"
            required
          >
            <option value="">Choose region…</option>
            {TANZANIA_REGIONS.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}
              </option>
            ))}
          </select>
        </label>
        <label htmlFor={`${idPrefix}branch-district`}>
          District (Wilaya)
          <select
            id={`${idPrefix}branch-district`}
            value={form.district}
            onChange={(e) => setForm((f) => ({ ...f, district: e.target.value }))}
            disabled={busy || !form.region}
            className="branches-select"
            required
          >
            <option value="">{form.region ? 'Choose district…' : 'Select region first'}</option>
            {districtOptions.map((d) => (
              <option key={d.id} value={d.id}>
                {d.name}
              </option>
            ))}
          </select>
        </label>
      </div>
      <label htmlFor={`${idPrefix}branch-street`}>
        Street / area (Mtaa)
        <input
          id={`${idPrefix}branch-street`}
          value={form.street}
          onChange={(e) => setForm((f) => ({ ...f, street: e.target.value }))}
          placeholder="e.g. Pamba Street, Industrial Area"
          disabled={busy}
        />
      </label>
    </>
  );
}

const BranchesPage = ({ onNavigate, user }) => {
  const [branches, setBranches] = useState([]);
  const [maxBranches, setMaxBranches] = useState(1);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState(emptyBranchForm);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState(emptyBranchForm);

  const isOwner = normalizeRole(user?.role) === 'owner';
  const canAddMore = branches.length < maxBranches;
  const editingBranch = branches.find((b) => b.id === editingId) || null;

  const districtOptions = form.region ? getDistrictsByRegion(form.region) : [];
  const editDistrictOptions = editForm.region ? getDistrictsByRegion(editForm.region) : [];

  const load = useCallback(async () => {
    setLoading(true);
    setMsg(null);
    try {
      const data = await apiJson('/owner/branches');
      setBranches(data.branches || []);
      setMaxBranches(data.maxBranches ?? 1);
    } catch (e) {
      setBranches([]);
      setMsg({ type: 'error', text: e.message || 'Could not load branches.' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOwner) void load();
    else setLoading(false);
  }, [isOwner, load]);

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm(emptyBranchForm());
  };

  const startEdit = (branch) => {
    setEditingId(branch.id);
    setEditForm(branchToForm(branch));
    setMsg(null);
    window.setTimeout(() => {
      document.getElementById('branches-edit-panel')?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, 50);
  };

  if (!isOwner) {
    return (
      <div className="page-bleed branches-page">
        <p>Only the business owner can manage branches.</p>
      </div>
    );
  }

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!canAddMore) {
      setMsg({
        type: 'error',
        text: `Your plan allows ${maxBranches} branch${maxBranches === 1 ? '' : 'es'}. Upgrade to add more.`,
      });
      return;
    }
    setBusy(true);
    setMsg(null);
    try {
      const res = await apiFetch('/owner/branches', {
        method: 'POST',
        body: JSON.stringify({
          name: form.name.trim(),
          region: form.region || null,
          district: form.district || null,
          street: form.street?.trim() || null,
        }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(body.message || 'Could not add branch.');
      }
      setForm(emptyBranchForm());
      await load();
      setMsg({
        type: 'success',
        text: `Branch "${body.branch?.name || form.name}" added. Assign workers from Add user or the table below.`,
      });
    } catch (err) {
      setMsg({ type: 'error', text: err.message || 'Save failed.' });
    } finally {
      setBusy(false);
    }
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    if (!editingId) return;
    setBusy(true);
    setMsg(null);
    try {
      const body = await apiJson(`/owner/branches/${editingId}`, {
        method: 'PATCH',
        body: JSON.stringify({
          name: editForm.name.trim(),
          region: editForm.region || null,
          district: editForm.district || null,
          street: editForm.street?.trim() || null,
        }),
      });
      await load();
      cancelEdit();
      setMsg({
        type: 'success',
        text: `Branch "${body.branch?.name || editForm.name}" updated.`,
      });
    } catch (err) {
      setMsg({ type: 'error', text: err.message || 'Update failed.' });
    } finally {
      setBusy(false);
    }
  };

  const toggleActive = async (branch) => {
    if (branch.isDefault) return;
    setBusy(true);
    try {
      await apiJson(`/owner/branches/${branch.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ isActive: !branch.isActive }),
      });
      await load();
      if (editingId === branch.id) cancelEdit();
    } catch (err) {
      setMsg({ type: 'error', text: err.message || 'Update failed.' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="page-bleed branches-page">
      <h1 className="branches-page__title">Branches</h1>
      <p className="branches-page__lead">
        Add plant or depot locations. Each branch has its own workers — staff only see teammates at their branch.
      </p>
      <p className="branches-page__meta">
        Using <strong>{branches.length}</strong> of <strong>{maxBranches}</strong> branch
        {maxBranches === 1 ? '' : 'es'} on your plan.
        {!canAddMore ? (
          <>
            {' '}
            <button type="button" className="branches-inline-link" onClick={() => onNavigate('subscription')}>
              Upgrade plan
            </button>{' '}
            for more locations.
          </>
        ) : null}
      </p>

      {msg ? <div className={`branches-msg branches-msg--${msg.type}`}>{msg.text}</div> : null}

      <section className="branches-card">
        <h2>Add branch</h2>
        {canAddMore ? (
          <form className="branches-form" onSubmit={handleAdd}>
            <BranchFormFields
              form={form}
              setForm={setForm}
              busy={busy}
              districtOptions={districtOptions}
              idPrefix="add-"
            />
            <button type="submit" className="branches-btn branches-btn--primary" disabled={busy}>
              {busy ? 'Saving…' : 'Add branch'}
            </button>
          </form>
        ) : (
          <p className="branches-limit-note">
            You have used all branches on your current plan. Open{' '}
            <button type="button" className="branches-inline-link" onClick={() => onNavigate('subscription')}>
              Subscription
            </button>{' '}
            to upgrade, or deactivate an unused branch first.
          </p>
        )}
      </section>

      {editingBranch ? (
        <section id="branches-edit-panel" className="branches-card branches-card--edit">
          <div className="branches-edit-head">
            <h2>
              Edit branch
              {editingBranch.isDefault ? (
                <span className="branches-badge branches-badge--inline">Main</span>
              ) : null}
            </h2>
            <button type="button" className="branches-btn branches-btn--sm branches-btn--ghost" onClick={cancelEdit} disabled={busy}>
              Cancel
            </button>
          </div>
          <form className="branches-form branches-form--edit" onSubmit={handleSaveEdit}>
            <BranchFormFields
              form={editForm}
              setForm={setEditForm}
              busy={busy}
              districtOptions={editDistrictOptions}
              idPrefix="edit-"
            />
            <div className="branches-form-actions">
              <button type="submit" className="branches-btn branches-btn--primary" disabled={busy}>
                {busy ? 'Saving…' : 'Save changes'}
              </button>
              <button type="button" className="branches-btn branches-btn--ghost" onClick={cancelEdit} disabled={busy}>
                Cancel
              </button>
            </div>
          </form>
        </section>
      ) : null}

      <section className="branches-card branches-card--table">
        <h2>All branches</h2>
        {loading ? <p className="branches-loading">Loading branches…</p> : null}
        {!loading && branches.length === 0 ? (
          <p className="branches-empty">
            No branches found. Refresh the page — a default &quot;Main&quot; branch should appear automatically.
            <button type="button" className="branches-btn branches-btn--primary branches-empty__btn" onClick={() => load()}>
              Refresh list
            </button>
          </p>
        ) : null}
        {!loading && branches.length > 0 ? (
          <div className="branches-table-wrap">
            <table className="branches-table">
              <thead>
                <tr>
                  <th className="branches-col-name">Name</th>
                  <th className="branches-col-region">Region (Mkoa)</th>
                  <th className="branches-col-district">District (Wilaya)</th>
                  <th className="branches-col-street">Street (Mtaa)</th>
                  <th className="branches-col-staff">Staff</th>
                  <th className="branches-col-status">Status</th>
                  <th className="branches-col-actions">Actions</th>
                </tr>
              </thead>
              <tbody>
                {branches.map((b) => {
                  const isEditing = editingId === b.id;
                  return (
                    <tr key={b.id} className={isEditing ? 'branches-row--editing' : undefined}>
                      <td className="branches-col-name">
                        <div className="branches-name-cell">
                          <strong className="branches-name">{b.name}</strong>
                          {b.isDefault ? <span className="branches-badge">Main</span> : null}
                        </div>
                      </td>
                      <td className="branches-col-region">
                        {getRegionName(b.region) || <span className="branches-empty-cell">—</span>}
                      </td>
                      <td className="branches-col-district">
                        {getDistrictNameResolved(b.region, b.district) || (
                          <span className="branches-empty-cell">—</span>
                        )}
                      </td>
                      <td className="branches-col-street">
                        {b.street?.trim() ? (
                          <span className="branches-street" title={b.street}>
                            {b.street}
                          </span>
                        ) : (
                          <span className="branches-empty-cell">—</span>
                        )}
                      </td>
                      <td className="branches-col-staff">{b.staffCount ?? 0}</td>
                      <td className="branches-col-status">
                        <span
                          className={`branches-status ${b.isActive ? 'branches-status--active' : 'branches-status--inactive'}`}
                        >
                          {b.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="branches-actions">
                        <button
                          type="button"
                          className={`branches-btn branches-btn--sm${isEditing ? ' branches-btn--active' : ''}`}
                          disabled={busy}
                          onClick={() => (isEditing ? cancelEdit() : startEdit(b))}
                        >
                          {isEditing ? 'Editing…' : 'Edit'}
                        </button>
                        <button
                          type="button"
                          className="branches-btn branches-btn--sm"
                          disabled={busy}
                          onClick={() => {
                            try {
                              sessionStorage.setItem('stocktrack_add_user_branch', String(b.id));
                            } catch {
                              /* ignore */
                            }
                            onNavigate('users-add');
                          }}
                        >
                          Add worker
                        </button>
                        {!b.isDefault ? (
                          <button
                            type="button"
                            className="branches-btn branches-btn--sm branches-btn--ghost"
                            disabled={busy}
                            onClick={() => toggleActive(b)}
                          >
                            {b.isActive ? 'Deactivate' : 'Activate'}
                          </button>
                        ) : null}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : null}
      </section>
    </div>
  );
};

export default BranchesPage;
